import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import {
  createCategory,
  updateCategory,
  uploadCategoryImageFile,
} from '../../services/admin/categories';

const CategoryForm = ({ category, categories, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(category?.image || null);

  // Filter out the current category and its descendants from parent options
  const getAvailableParents = () => {
    if (!category) return categories.filter((c) => !c.parentId);
    
    // Prevent selecting self or descendants as parent
    const excludeIds = new Set([category.id]);
    const getDescendants = (catId) => {
      categories.forEach((c) => {
        if (c.parentId === catId) {
          excludeIds.add(c.id);
          getDescendants(c.id);
        }
      });
    };
    getDescendants(category.id);
    
    return categories.filter((c) => !excludeIds.has(c.id));
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Kategori adı zorunludur'),
    slug: Yup.string()
      .required('Slug zorunludur')
      .matches(/^[a-z0-9-]+$/, 'Slug sadece küçük harf, rakam ve tire içerebilir'),
    order: Yup.number().min(0, 'Sıra 0 veya daha büyük olmalıdır'),
  });

  const formik = useFormik({
    initialValues: {
      name: category?.name || '',
      slug: category?.slug || '',
      parentId: category?.parentId ? String(category.parentId) : '',
      description: category?.description || '',
      image: category?.image || '',
      order: category?.order || 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setUploadingImage(true);

        const categoryData = {
          ...values,
          image: values.image,
          parentId: values.parentId ? parseInt(values.parentId, 10) : null,
          order: parseInt(values.order) || 0,
        };

        if (category?.id) {
          if (imageFile) {
            const imageUrl = await uploadCategoryImageFile(imageFile, category.id);
            categoryData.image = imageUrl;
          }
          await updateCategory(category.id, categoryData);
          toast({
            title: 'Başarılı',
            description: 'Kategori başarıyla güncellendi.',
          });
        } else {
          const created = await createCategory(categoryData);
          if (imageFile && created?.id) {
            await uploadCategoryImageFile(imageFile, created.id);
          }
          toast({
            title: 'Başarılı',
            description: 'Kategori başarıyla oluşturuldu.',
          });
        }

        onSuccess();
      } catch (error) {
        console.error('Error saving category:', error);
        toast({
          title: 'Hata',
          description: 'Kategori kaydedilirken bir hata oluştu.',
          variant: 'destructive',
        });
      } finally {
        setUploadingImage(false);
      }
    },
  });

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (e) => {
    formik.handleChange(e);
    // Auto-generate slug if slug is empty or matches the old name's slug
    if (!formik.values.slug || formik.values.slug === generateSlug(formik.values.name)) {
      formik.setFieldValue('slug', generateSlug(e.target.value));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Hata',
          description: 'Lütfen bir resim dosyası seçin.',
          variant: 'destructive',
        });
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    formik.setFieldValue('image', '');
  };

  const availableParents = getAvailableParents();

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Temel Bilgiler</h3>

        <div>
          <Label htmlFor="name">Kategori Adı *</Label>
          <Input
            id="name"
            name="name"
            value={formik.values.name}
            onChange={handleNameChange}
            onBlur={formik.handleBlur}
            placeholder="Örn: Kadın"
            className={formik.errors.name && formik.touched.name ? 'border-red-500' : ''}
          />
          {formik.errors.name && formik.touched.name && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.name}</p>
          )}
        </div>

        <div>
          <Label htmlFor="slug">Slug *</Label>
          <Input
            id="slug"
            name="slug"
            value={formik.values.slug}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Örn: kadin"
            className={formik.errors.slug && formik.touched.slug ? 'border-red-500' : ''}
          />
          {formik.errors.slug && formik.touched.slug && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.slug}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            URL'de kullanılacak kısa isim (örn: /kategori/kadin)
          </p>
        </div>

        <div>
          <Label htmlFor="parentId">Üst Kategori</Label>
          <Select
            value={formik.values.parentId || 'none'}
            onValueChange={(value) => {
              formik.setFieldValue('parentId', value === 'none' ? null : value);
            }}
          >
            <SelectTrigger id="parentId">
              <SelectValue placeholder="Ana kategori (boş bırakabilirsiniz)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Ana Kategori (Üst kategori yok)</SelectItem>
              {availableParents.map((cat) => (
                <SelectItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500 mt-1">
            Bu kategoriyi bir üst kategoriye bağlamak için seçin
          </p>
        </div>

        <div>
          <Label htmlFor="description">Açıklama</Label>
          <Textarea
            id="description"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            rows={3}
            placeholder="Kategori açıklaması (isteğe bağlı)"
          />
        </div>

        <div>
          <Label htmlFor="order">Sıra</Label>
          <Input
            id="order"
            name="order"
            type="number"
            min="0"
            value={formik.values.order}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="0"
            className={formik.errors.order && formik.touched.order ? 'border-red-500' : ''}
          />
          {formik.errors.order && formik.touched.order && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.order}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Kategorilerin listelenme sırası (düşük sayı önce gösterilir)
          </p>
        </div>
      </div>

      {/* Image Upload */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Kategori Görseli</h3>

        {imagePreview ? (
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Category preview"
              className="w-64 h-48 object-cover rounded border"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="w-64 h-48 bg-gray-100 rounded border flex items-center justify-center">
            <ImageIcon className="text-gray-400" size={48} />
          </div>
        )}

        <div>
          <Label htmlFor="image">Görsel Yükle</Label>
          <Input
            id="image"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="mt-1"
          />
          <p className="text-sm text-gray-500 mt-1">
            Kategori banner/thumbnail görseli (önerilen: 800x600px)
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          İptal
        </Button>
        <Button type="submit" disabled={uploadingImage}>
          {uploadingImage ? 'Kaydediliyor...' : category ? 'Güncelle' : 'Oluştur'}
        </Button>
      </div>
    </form>
  );
};

export default CategoryForm;
